/*
 * Copyright (c) 2022 CalAmp Corp. All Rights Reserved
 */
import {dlsColor} from '../theme/theme';

const getStatusColors = () => ({
  valid : {
    textColor : dlsColor('caGreen'),
    color     : 'rgba(105, 178, 70, 0.1)'
  },
  invalid : {
    textColor : dlsColor('caRed'),
    color     : 'rgba(226, 35, 26, 0.1)'
  },
  other : {
    textColor : dlsColor('grey2'),
    color     : 'rgba(79, 79, 79, 0.1)'
  }
});

export interface UseStatusBadgeColorsProps {
  status: string;
  display?: string;
  positiveStatuses?: string[];
  negativeStatuses?: string[];
  isPadding?: boolean;
}

/**
 * Re-usable hook for determining badge colors.
 *
 * Given a status custom positive or negative statuses, will return either:
 *    1) Green: when the given status correlates to a positive status
 *    2) Red: when the given status correlates to a negative status
 *    3) Gray: for all others
 *
 * ```typescript
 *
 *    import {useStatusBadgeColors} from '@ion/dls/src/hooks/';
 *
 *    const {color, textColor} = useStatusBadgeColors({
 *      status, // 'COMPLETED', 'CANCELLED', etc...
 *      positiveStatuses : [Status.COMPLETED],
 *      negativeStatuses : [Status.FAILED, Status.CANCELLED]
 *    });
 *
 * ```
 */
export const useStatusBadgeColors = ({
  status,
  positiveStatuses,
  negativeStatuses
}: UseStatusBadgeColorsProps) => {
  const statusColors = getStatusColors();
  if (positiveStatuses.includes(status)) {
    return statusColors.valid;
  }
  if (negativeStatuses.includes(status)) {
    return statusColors.invalid;
  }
  return statusColors.other;
};

export default useStatusBadgeColors;
